import { useState, useEffect } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { api } from '../../lib/api';
import { formatDate, getStatusColor, WASTE_STREAM_LABELS } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
  Building2,
  Calendar,
  FileText,
  CheckCircle2,
  Clock,
  Upload,
  LogOut,
} from 'lucide-react';

export function VendorPortalPage() {
  const { signOut } = useClerk();
  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState(null);
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    // In a real app, this would fetch the vendor based on the logged-in user's vendor_id
    setLoading(false);
  }, []);

  const handleConfirmCollection = async (eventId) => {
    try {
      await api.updateCollectionEventStatus(eventId, 'confirmed');
      toast.success('Collection confirmed');
    } catch (error) {
      toast.error('Failed to confirm collection');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-white py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Vendor Portal</h1>
              <p className="text-sm text-white/70">Mind Your Waste</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="text-white hover:bg-white/10"
            onClick={() => signOut()}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Welcome Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                <Building2 className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground">Welcome to the Vendor Portal</h2>
                <p className="text-muted-foreground">
                  Manage your documents, view assignments, and confirm collections
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Qualification Status */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Qualification Checklist
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Progress</span>
                  <span className="font-medium">60%</span>
                </div>
                <Progress value={60} />
              </div>

              <div className="space-y-3">
                {[
                  { name: 'NCA/Letter of Intent', status: 'approved' },
                  { name: 'Waste Registration', status: 'approved' },
                  { name: 'Pricing', status: 'approved' },
                  { name: 'Timeline Confirmed', status: 'pending' },
                  { name: 'Insurance Certification', status: 'pending' },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 border border-border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {item.status === 'approved' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-yellow-600" />
                      )}
                      <span>{item.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getStatusColor(item.status)}>
                        {item.status}
                      </Badge>
                      {item.status === 'pending' && (
                        <Button size="sm" variant="outline">
                          <Upload className="w-4 h-4 mr-1" />
                          Upload
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-3xl font-bold text-foreground">5</p>
                  <p className="text-sm text-muted-foreground">Upcoming Collections</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="text-3xl font-bold text-foreground">85%</p>
                  <p className="text-sm text-muted-foreground">Performance Score</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Upcoming Collections */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Upcoming Collections
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { store: 'Amsterdam Central Store', date: '2026-01-20', stream: 'cardboard', status: 'scheduled' },
                { store: 'Rotterdam Retail Hub', date: '2026-01-21', stream: 'general', status: 'scheduled' },
                { store: 'The Hague Mall', date: '2026-01-22', stream: 'plastics', status: 'confirmed' },
              ].map((collection, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-4 border border-border rounded-lg"
                >
                  <div>
                    <p className="font-medium">{collection.store}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(collection.date)} • {WASTE_STREAM_LABELS[collection.stream]}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={getStatusColor(collection.status)}>
                      {collection.status}
                    </Badge>
                    {collection.status === 'scheduled' && (
                      <Button
                        size="sm"
                        onClick={() => toast.success('Collection confirmed')}
                      >
                        Confirm
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
